import { Add, Clear } from "@mui/icons-material"
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Divider,
    Grid2 as Grid,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Stack,
    TextField,
    Typography,
} from "@mui/material"
import { useEffect, useState } from "react"
import { useFindAcquisitionModes, useFindAcquisitionModesForFlightPlan } from "../../api/AcquisitionModeApi"
import { NumberField } from "../../components/Fields/NumberField"
import { AcquisitionModeDetailsStyledBackground } from "../../components/Modes/ModeView"
import { Label } from "../../components/Typography/Typography"
import {
    AcquisitionMode,
    AcquisitionModeInFlightplan,
    FlightplanSensorConfiguration,
    ModeType,
} from "../../models/AcquisitionMode"
import { useFlightplanEditStore } from "../../zstore/FlightplanEditStore"

export const FlightplanEditorSelectAcquisitionModes = ({ flightplanId }: { flightplanId?: number }) => {
    // Fetch the data necessary here to improve React rerender cycle efficiency.
    // When this all runs with the below component, rerendering is very slow.
    const acquisitionModesResponse = useFindAcquisitionModes()
    const acquisitionModesForFlightPlanResponse = useFindAcquisitionModesForFlightPlan(flightplanId)

    const initialSelectedModes = acquisitionModesForFlightPlanResponse.data
    const availableModes = acquisitionModesResponse.data
    const loading =
        acquisitionModesResponse.isLoading || acquisitionModesForFlightPlanResponse.isLoading || availableModes == null

    if (loading) {
        return <CircularProgress />
    }

    return (
        <FlightplanEditorSelectAcquisitionModesForm
            availableModes={availableModes}
            initialSelectedModes={initialSelectedModes}
        />
    )
}

const FlightplanEditorSelectAcquisitionModesForm = ({
    availableModes,
    initialSelectedModes,
}: {
    availableModes: AcquisitionMode[]
    initialSelectedModes?: AcquisitionModeInFlightplan[]
}) => {
    const initializeModes = useFlightplanEditStore((state) => state.initializeModes)
    const setAvailableModes = useFlightplanEditStore((state) => state.setAvailableModes)
    const resetAcquisitionModes = useFlightplanEditStore((state) => state.resetAcquisitionModes)
    const toggleSelectedMode = useFlightplanEditStore((state) => state.toggleSelectedMode)
    const modesByType = useFlightplanEditStore((state) => state.modesByType)
    const viewAcqMode = useFlightplanEditStore((state) => state.viewAcqMode)
    const setViewAcqMode = useFlightplanEditStore((state) => state.setViewAcqMode)

    const [selectedModeIds, setSelectedModeIds] = useState<number[]>(
        initialSelectedModes?.map((mode) => mode.mode_id) ?? [],
    )
    const selected = viewAcqMode == null ? false : selectedModeIds.includes(viewAcqMode.mode.mode_id)

    useEffect(() => {
        resetAcquisitionModes()
    }, [])

    useEffect(() => {
        if (availableModes != null) {
            setAvailableModes(availableModes)
        }
    }, [availableModes])

    useEffect(() => {
        if (initialSelectedModes != null) {
            initializeModes(initialSelectedModes)
        }
    }, [initialSelectedModes])

    return (
        <>
            <Label gutterBottom="sm">Acquisition Modes & Sensor Configuration</Label>
            <Grid container={true} spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <List dense sx={{ width: "100%", maxWidth: 360, bgcolor: "background.paper" }}>
                        {modesByType.map((modesObj) => (
                            <Box key={modesObj.mode_type}>
                                <Typography>
                                    <strong>{modesObj.mode_type}</strong>
                                </Typography>
                                <Divider />
                                {modesObj.modes.map((acquisitionMode) => {
                                    const labelId = `acquisition-mode-select-checkbox-label-${acquisitionMode.mode_id}`
                                    return (
                                        <ListItem
                                            key={acquisitionMode.mode_id}
                                            secondaryAction={
                                                <Checkbox
                                                    edge="end"
                                                    onChange={async () => {
                                                        if (selectedModeIds.includes(acquisitionMode.mode_id)) {
                                                            setSelectedModeIds(
                                                                selectedModeIds.filter(
                                                                    (modeId) => modeId !== acquisitionMode.mode_id,
                                                                ),
                                                            )
                                                        } else {
                                                            const updatedIds = selectedModeIds.slice()
                                                            updatedIds.push(acquisitionMode.mode_id)
                                                            setSelectedModeIds(updatedIds)
                                                        }
                                                        await setViewAcqMode(acquisitionMode)
                                                        await toggleSelectedMode(acquisitionMode.mode_id)
                                                    }}
                                                    checked={
                                                        selectedModeIds.find(
                                                            (modeId) => modeId === acquisitionMode.mode_id,
                                                        ) != null
                                                    }
                                                    slotProps={{
                                                        input: {
                                                            "aria-labelledby": labelId,
                                                        },
                                                    }}
                                                />
                                            }
                                            disablePadding
                                        >
                                            <ListItemButton
                                                onClick={() => setViewAcqMode(acquisitionMode)}
                                                selected={viewAcqMode?.mode.mode_id === acquisitionMode.mode_id}
                                            >
                                                <ListItemText id={labelId} primary={acquisitionMode.mode_name} />
                                            </ListItemButton>
                                        </ListItem>
                                    )
                                })}
                            </Box>
                        ))}
                    </List>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 9 }}>
                    <UpdateAcquisitionModeDetails selected={selected} />
                </Grid>
            </Grid>
        </>
    )
}

const UpdateAcquisitionModeDetails = (props: { selected: boolean }) => {
    const viewAcqMode = useFlightplanEditStore((state) => state.viewAcqMode)
    const addConfigForMode = useFlightplanEditStore((state) => state.addConfigForMode)

    return (
        <AcquisitionModeDetailsStyledBackground disabled={true}>
            {viewAcqMode != null && (
                <Stack>
                    <Label>Sensor Configurations for {viewAcqMode.mode.mode_name}</Label>
                    {viewAcqMode.configs.map((sensorConfig, idx) => {
                        return (
                            <SensorConfigForm
                                key={idx}
                                mode={viewAcqMode.mode}
                                configIdx={idx}
                                config={sensorConfig}
                                selected={props.selected}
                                configsCount={viewAcqMode.configs.length}
                            />
                        )
                    })}
                    <Box display={"flex"}>
                        <Button disabled={!props.selected} onClick={() => addConfigForMode(viewAcqMode.mode.mode_id)}>
                            <Add /> Additional Sensor Configuration
                        </Button>
                    </Box>
                </Stack>
            )}
        </AcquisitionModeDetailsStyledBackground>
    )
}

const SensorConfigForm = ({
    mode,
    configIdx,
    config,
    selected,
    configsCount,
}: {
    mode: AcquisitionMode
    configIdx: number
    config: FlightplanSensorConfiguration
    selected: boolean
    configsCount: number
}) => {
    const updateConfigForMode = useFlightplanEditStore((state) => state.updateConfigForMode)
    const removeConfigForMode = useFlightplanEditStore((state) => state.removeConfigForMode)
    const isLidar = mode.mode_type === ModeType.PtCloud

    return (
        <Grid container={true} spacing={2} mb={2}>
            <Grid size={{ xs: 12 }}>
                <Box display={"flex"} alignItems={"center"}>
                    <Typography variant={"subtitle2"}>Config #{configIdx + 1}</Typography>
                    <IconButton
                        sx={{ ml: 1 }}
                        size="small"
                        disabled={!selected || configsCount === 1}
                        onClick={() => removeConfigForMode(configIdx)}
                    >
                        <Clear />
                    </IconButton>
                </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 4, xl: 3 }}>
                <TextField
                    fullWidth={true}
                    size={"small"}
                    value={config.name}
                    label={"Configuration Name"}
                    disabled={!selected}
                    required={true}
                    onChange={async (event) => {
                        await updateConfigForMode(mode.mode_id, configIdx, {
                            name: event.target.value,
                        })
                    }}
                />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 4, xl: 3 }}>
                <NumberField
                    fullWidth={true}
                    value={config.target_agl_meters}
                    label={"Target AGL in Meters"}
                    disabled={!selected}
                    onChange={async (value) => {
                        await updateConfigForMode(mode.mode_id, configIdx, {
                            target_agl_meters: value ?? undefined,
                        })
                    }}
                />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 4, xl: 3 }}>
                <NumberField
                    fullWidth={true}
                    value={config.target_speed_km_per_h}
                    label={"Target Speed km/hour"}
                    disabled={!selected}
                    onChange={async (value) => {
                        await updateConfigForMode(mode.mode_id, configIdx, {
                            target_speed_km_per_h: value ?? undefined,
                        })
                    }}
                />
            </Grid>
            {isLidar && (
                <>
                    <Grid size={{ xs: 12, md: 6, lg: 4, xl: 3 }}>
                        <NumberField
                            fullWidth={true}
                            value={config.laser_prr}
                            label={"Laser PRR"}
                            decimalCount={0}
                            disabled={!selected}
                            onChange={async (value) => {
                                await updateConfigForMode(mode.mode_id, configIdx, {
                                    laser_prr: value ?? undefined,
                                })
                            }}
                            helperText={"Integers only"}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, lg: 4, xl: 3 }}>
                        <NumberField
                            fullWidth={true}
                            value={config.lps}
                            label={"LPS"}
                            disabled={!selected}
                            onChange={async (value) => {
                                await updateConfigForMode(mode.mode_id, configIdx, {
                                    lps: value ?? undefined,
                                })
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, lg: 4, xl: 3 }}>
                        <NumberField
                            fullWidth={true}
                            value={config.fov_degrees}
                            label={"FOV Degrees"}
                            disabled={!selected}
                            onChange={async (value) => {
                                await updateConfigForMode(mode.mode_id, configIdx, {
                                    fov_degrees: value ?? undefined,
                                })
                            }}
                        />
                    </Grid>
                </>
            )}
        </Grid>
    )
}
